/**
 * Uses the performance.mark browser API to mark when events happen in the
 * browser.
 * @param  {String} name The name of the event we want to mark.
 */
export function performanceMark(name) {
  if (typeof performance === 'object' && typeof performance.mark === 'function') {
    performance.mark(`ad_${name}`);
    // Invalidate cache when a new mark is added to ensure accuracy
    cachedPerformanceMarks = null;
  }
  return true;
}

/**
 * Checks for advertising consent within the OneTrust OptanonConsent cookie
 * @returns {Boolean}
 */
export function checkOneTrustCookieConsent() {
  try {
    // If OneTrust object exists, use it directly
    if (window.OnetrustActiveGroups) {
      return window.OnetrustActiveGroups.includes('C0003');
    }

    // Fallback to cookie check
    const match = document.cookie.match(/OptanonConsent=([^;]+)/);
    if (match) {
      const decodedValue = decodeURIComponent(match[1]);
      const groupsMatch = decodedValue.match(/groups=([^&]+)/);
      if (groupsMatch) {
        const groups = groupsMatch[1];
        return groups.includes('C0003:1');
      }
    }

    return false;
  } catch (error) {
    return false;
  }
}

/**
 * Uses the performance.mark browser API to mark when events happen in the
 * browser.
 * @param  {String} name The name of the event we want to mark.
 */
export function performanceMeasure(name, start, end) {
  if (!start || !end) {
    var generated = getPerformanceMeasureDefaults(name);
    start = start || generated.start;
    end = end || generated.end;
  }
  if (typeof performance === 'object' && typeof performance.measure === 'function') {
    if (anyPerformanceMarksAreUndefined(`ad_${start}`, `ad_${end}`)) {
      return true;
    }

    performance.measure(`ad_${name}`, `ad_${start}`, `ad_${end}`);
  }
  return true;
}

/**
 * Cache for performance marks to avoid repeated expensive API calls
 * @type {Set<string>|null}
 */
let cachedPerformanceMarks = null;
let cachedMarksTimestamp = 0;
const MARKS_CACHE_TTL = 100; // Cache for 100ms to balance freshness and performance

/**
 * Test if any performance marks are not currently defined on the page
 * Uses caching and Set-based lookups for optimal performance
 * @param {String} start
 * @param {String} end
 * @returns {Boolean} True if either mark is undefined
 */
function anyPerformanceMarksAreUndefined(start, end) {
  const now = Date.now();

  // Refresh cache if expired or not initialized
  if (!cachedPerformanceMarks || now - cachedMarksTimestamp > MARKS_CACHE_TTL) {
    const marksOnPage = performance.getEntriesByType('mark');
    // Use Set for O(1) lookups instead of array iteration
    cachedPerformanceMarks = new Set(marksOnPage.map(entry => entry.name));
    cachedMarksTimestamp = now;
  }

  // Check if either mark is missing using Set.has() for O(1) lookup
  return !cachedPerformanceMarks.has(start) || !cachedPerformanceMarks.has(end);
}

/**
 * A quick test to know whether a value/string includes PII. Used to know whether
 * we can send ads for a page.
 * @param  {string} value A value that be tested as an argument in `RexExp.test`
 * @return {boolean}
 */
export function containsPii(value) {
  const PII_REGEX = /\S+(@|%40)\S+\.\S+/;
  return PII_REGEX.test(value);
}

/**
 * Converts snake_case or kebab-case to PascalCase
 * @param  {string} value A value to be converted
 * @return {string}
 */
export function convertToPascal(string) {
  const SNAKE_REGEX = /([-_][a-z])/gi;

  const capitalizedString = string.charAt(0).toUpperCase() + string.slice(1);
  return capitalizedString.replace(SNAKE_REGEX, val => {
    return val
      .toUpperCase()
      .replace('_', '')
      .replace('-', '');
  });
}

/**
 * A quick test to know whether we should use transitionEnd or webkitTransitionEnd
 * @return {string}
 */
export function transitionEndEvent() {
  var el = document.createElement('div');

  var transitions = {
    transition: 'transitionend',
    WebkitTransition: 'webkitTransitionEnd',
  };

  for (var t in transitions) {
    if (el.style[t] !== undefined) {
      return transitions[t];
    }
  }
}

/**
 * Debounces a function to only run after repeated invocations have stopped.
 * @param {Function} function to debounce.
 * @param {Number} time in milliseconds to delay invocation.
 * @param {Object} binding context in which the function is invoked.
 * @returns {Function} the debounced function.
 */
export function debounce(fn, delay, context) {
  var timer;
  return function() {
    var callTarget = context || this;
    var callArgs = arguments;
    clearTimeout(timer);
    timer = setTimeout(function() {
      fn.apply(callTarget, callArgs);
    }, delay);
  };
}

/**
 * Gets the current viewport width.
 * @return {Integer} Viewport width
 */
export function getViewportWidth() {
  return Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
}

/**
 * Gets the canonical url
 * @param {string} url
 * @return {string} url
 */
export function getCanonicalUrl(url = null) {
  const canonicalElement = document.querySelector('link[rel="canonical"][href]');
  const canonicalURL = canonicalElement ? canonicalElement.href : null;
  return url || canonicalURL || window.location.href;
}

/**
 * Determines device type using modern detection methods.
 * Uses pointer media queries (more accurate than touch detection) combined with screen size.
 * Breakpoints match CSS conventions: mobile < 768, tablet 768-1180, desktop 1181+
 *
 * @return {String} Device type: 'mobile', 'tablet', or 'desktop'
 */
export function getDeviceType() {
  // Check iframe override first (for preview mode in Anthem)
  try {
    if (window.frameElement?.getAttribute('data-device-type')) {
      return window.frameElement.getAttribute('data-device-type');
    }
  } catch (e) {
    // Swallow security errors from cross-origin iframe access
  }

  // Check URL overrides (for testing)
  if (/mobile=true/.test(window.location.search)) {
    return 'mobile';
  }
  if (/tablet=true/.test(window.location.search)) {
    return 'tablet';
  }

  const width = window.innerWidth;

  // Modern detection: pointer type + screen size
  // 'pointer: coarse' indicates a touch/finger input (vs fine = mouse)
  if (window.matchMedia) {
    const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;

    // Mobile: touch device with small screen
    if (hasCoarsePointer && width < 768) {
      return 'mobile';
    }

    // Tablet: touch device with medium screen
    if (hasCoarsePointer && width < 1181) {
      return 'tablet';
    }
  } else {
    // Fallback for browsers without matchMedia (very old browsers)
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    if (isTouch && width < 768) {
      return 'mobile';
    }
    if (isTouch && width < 1181) {
      return 'tablet';
    }
  }

  return 'desktop';
}

/**
 * Detect an in-app mobile WebView request via Duet’s `in_app` dfpVariable.
 * Accepts either app.settings (reads `dfpVariables.in_app`) or a variables
 * object with `in_app` at the top level. Values may be boolean, string, or array.
 * @param {Object} [settingsOrVariables]
 * @return {Boolean}
 */
export function isMobileWebView(settingsOrVariables) {
  if (!settingsOrVariables) {
    return false;
  }

  const value =
    settingsOrVariables.in_app !== undefined
      ? settingsOrVariables.in_app
      : settingsOrVariables.dfpVariables && settingsOrVariables.dfpVariables.in_app;
  const normalized = Array.isArray(value) ? value[0] : value;
  return normalized === true || normalized === 'true';
}

/**
 * A test to see if a User Agent string contains a webview-like agent
 * @param  {String} uastring  User agent string
 * @return {String}           The type of user agent, or false
 */
export function getWebView(uastring) {
  let webview = false;

  if (uastring.match(/Twitter/)) {
    webview = 'twitter';
  } else if (uastring.search('FBAN') >= 0) {
    webview = 'facebook';
  } else if (uastring.match(/Pinterest/)) {
    webview = 'pinterest';
  } else if (uastring.match(/AppleWebKit/) && !uastring.match(/Safari/)) {
    webview = 'undetermined';
  }

  return webview;
}

export const SESSION_DEPTH_KEY = 'ChorusSessionDepth';

/**
 * Get the a stored session depth.
 *
 * @return {Integer} Session depth
 */
export function getSessionDepth() {
  let sessionDepth = 0;

  try {
    sessionDepth = sessionStorage.getItem(SESSION_DEPTH_KEY) || 0;
  } catch (e) {
    // Do nothing
  }

  return sessionDepth;
}

/**
 * Increment the session depth.
 */
export function incrementSessionDepth() {
  let sessionDepth = getSessionDepth();

  try {
    sessionStorage.setItem(SESSION_DEPTH_KEY, ++sessionDepth);
  } catch (e) {
    // Do nothing
  }
}

/**
 * Calculates the top of the elements offset from the bottom of the viewport
 * as a fraction of the total viewport height.
 * A negative number denotes below the viewport, whereas a positive number
 * is the distance from the elements top to the bottom of the viewport
 * A number above 1.0 means it might no longer be in the viewport
 *
 * @param  {Dom Element} element An element to measure
 * @return {integer}  Distance of the top of element to bottom of viewport
 */
export function distanceFromBottom(element) {
  const viewportHeight = Math.max(window.innerHeight || 1);
  return (viewportHeight - element.getBoundingClientRect().top) / viewportHeight;
}

/**
 * Calculates the bottom of the elements offset from the bottom of the viewport
 * as a fraction of the total viewport height.
 * A negative number denotes below the viewport, whereas a positive number
 * is the distance from the elements bottom to the bottom of the viewport
 * A number above 1.0 means it might no longer be in the viewport
 *
 * @param  {Dom Element} element An element to measure
 * @return {integer}  Distance of the bottom of element to bottom of viewport
 */
export function distanceOfElementBottomFromViewportBottom(element) {
  const viewportHeight = Math.max(window.innerHeight || 1);
  return (viewportHeight - element.getBoundingClientRect().bottom) / viewportHeight;
}

/**
 * Given a number and set of options it will return the closes number
 * in the set of allowable options
 *
 * @param  {Number} input     the input number
 * @param  {Array} allowable  the allowable set of responses
 * @return {Number}           the closest matching number from the allowable responses
 */
export function closestTo(input, allowable = []) {
  const sorted = (allowable || []).sort((a, b) => {
    return a - b;
  });

  if (sorted.length == 0) {
    return input;
  }

  if (input < sorted[0]) {
    return sorted[0];
  }

  if (input > sorted[sorted.length - 1]) {
    return sorted[sorted.length - 1];
  }

  let closestProximity = Infinity;
  let closestAllowable = input;
  for (let i = 0; i < sorted.length; i++) {
    const proximity = Math.abs(input - sorted[i]);
    if (proximity < closestProximity) {
      closestProximity = proximity;
      closestAllowable = sorted[i];
    }
  }

  return closestAllowable;
}

/**
 * Extends a base object with properties from additional passed objects:
 * @param {Object} base object to extend.
 * @param {...Object} objects to extend properties from.
 * @returns {Object} the base object, extended by all other argument objects.
 */
export function extend(base) {
  for (var i = 1; i < arguments.length; i++) {
    var ext = arguments[i];
    for (var key in ext) {
      if (Object.prototype.hasOwnProperty.call(ext, key)) base[key] = ext[key];
    }
  }
  return base;
}

/**
 * Clones a base object. Allows you to create a copy of a nested object
 * without passing it by reference.
 * @param   {Object} Base Object
 * @returns {Object} Cloned object
 */
export function clone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Determines if the argument is a object
 * @returns {Boolean}
 */
export function isObject(obj) {
  return obj && typeof obj === 'object';
}

/**
 * Convenience function for dynamically generating measure keys
 * @param  {String} name The name of the event we want to mark.
 * @return {object} start/end names of measure
 */
function getPerformanceMeasureDefaults(name) {
  return {
    start: `${name}-start`,
    end: `${name}-end`,
  };
}

/**
 * Merges two objects together, if they share the same top level keys
 * then their values will be combined into an array, if their values are different
 *
 * This will merge the following two objects:
 *   {a: 1}, {a:2} --->     { a: [1, 2] }
 *   {a: 1}, {a:1} --->     { a: 1 }
 *   {a: [1,2]}, {a:3} ---> { a: [1, 2, 3] }
 *
 * @param {Object} original The base object
 * @param {Object} incoming The incoming object to merge and create arrays on overlapping keys
 * @return {Object} a merged object
 */
export function mergeByMakingArraysOfValuesWithTheSameKey(original, incoming) {
  let mergedResult = { ...incoming, ...original };
  const matchingKeys = Object.keys(incoming).filter(k => Object.keys(original).includes(k));

  matchingKeys.forEach(key => {
    if (mergedResult[key] !== incoming[key]) {
      if (Array.isArray(mergedResult[key])) {
        // Append the values to the existing array and filter out duplicates.
        mergedResult[key] = mergedResult[key].concat(incoming[key]).filter((val, i, all) => all.indexOf(val) === i);
      } else {
        // Create a 2 element array with the previous and incoming value.
        mergedResult[key] = [mergedResult[key], incoming[key]];
      }
    }
  });

  return mergedResult;
}

/**
 * Check if the DOM element is the boost article wrapper (direct match only)
 * @param {Element|Document} dom - The DOM element to check
 * @returns {Boolean} True if dom is the boost article wrapper
 */
function isInBoostArticleWrapper(dom) {
  if (!dom || dom === document) {
    return false;
  }
  // Only check if the element itself has the class (no ancestor checking)
  return dom.classList && dom.classList.contains('duet--article--boosted-post-wrapper');
}

export function conditionExists(conditional, settings, scopedDom = null) {
  try {
    if (conditional && typeof conditional.condition === 'object') {
      const conditionType = Object.keys(conditional.condition)[0];

      if (conditionType === 'useIfSubscription') {
        return Boolean(settings.hasSubscription);
      } else if (conditionType === 'useIfSelector') {
        const selectorValue = conditional.condition[conditionType];
        const searchDom = scopedDom || document;
        // Support both single selector (string) and multiple selectors (array)
        if (Array.isArray(selectorValue)) {
          // Return true if ANY selector exists (OR logic)
          return selectorValue.some(selector => searchDom.querySelector(selector) !== null);
        }
        // Single selector (backward compatible)
        return Boolean(searchDom.querySelector(selectorValue));
      } else if (conditionType === 'useIfLoggedIn') {
        return Boolean(settings.loggedIn);
      } else if (conditionType === 'useIfMobileWebView') {
        return isMobileWebView(settings) === Boolean(conditional.condition[conditionType]);
      } else if (conditionType === 'useIfInBoostContainer') {
        // Check if scopedDom is itself the boost article wrapper (direct match only)
        const isInBoostContainer = isInBoostArticleWrapper(scopedDom);
        return Boolean(conditional.condition[conditionType]) && isInBoostContainer;
      } else {
        return false;
      }
    }
    return false;
  } catch (e) {
    return false;
  }
}

/**
 * Checks if two arrays match
 * @param {Array} array1
 * @param {Array} array2
 * @return {Boolean}
 */
export function arraysMatch(array1, array2) {
  return JSON.stringify(array1) === JSON.stringify(array2);
}

/**
 * Detect devtools within the browser.
 */
export const devtools = window.__GOLDBUG_GLOBAL_HOOK__;
